import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { usePlantDetails } from '../hooks/usePlantDetails';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import { generatePlantBreadcrumb } from '../utils/breadcrumb';
import Breadcrumb from '../components/Breadcrumb';
import PlantHero from '../components/plants/detail/PlantHero';
import PlantDetailTabs from '../components/plants/detail/PlantDetailTabs';
import SowingPlanting from '../components/plants/detail/sections/SowingPlanting';
import Companions from '../components/plants/detail/sections/Companions';
import Varieties from '../components/plants/detail/sections/Varieties';
import MaintenanceCare from '../components/plants/detail/MaintenanceCare';
import Harvesting from '../components/plants/detail/sections/Harvesting';
import Storage from '../components/plants/detail/sections/Storage';
import SeasonalTips from '../components/plants/detail/sections/SeasonalTips';
import FavoriteUses from '../components/plants/detail/sections/FavoriteUses';
import GuideProductCard from '../components/plants/detail/GuideProductCard';
import PageProgress from '../components/ui/PageProgress';
import { Loader2, Book } from 'lucide-react';

export default function PlantDetail() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { plant, loading, error, isInGarden } = usePlantDetails(id);
  const { setItems } = useBreadcrumb();
  const [activeTab, setActiveTab] = useState('sowing');
  const [showScrollButton, setShowScrollButton] = useState(false);
  const guideRef = useRef<HTMLDivElement>(null);

  // Reset state when plant ID changes
  useEffect(() => {
    setActiveTab('sowing');
    setShowScrollButton(false);
  }, [id]);

  // Get tab from URL query params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [location.search]);

  // Set breadcrumb based on navigation source
  useEffect(() => {
    if (plant) {
      const source = location.state?.from;
      if (source?.type === 'collection') {
        setItems(generatePlantBreadcrumb(plant, {
          type: 'collection',
          collectionName: source.collectionName,
          collectionSlug: source.collectionSlug
        }));
      } else if (source?.type === 'garden') {
        setItems(generatePlantBreadcrumb(plant, { type: 'garden' }));
      } else {
        setItems(generatePlantBreadcrumb(plant));
      }
    }
  }, [plant, location.state, setItems]);

  // Scroll to companions section if coming from modal
  useEffect(() => {
    if (location.state?.scrollToCompanions) {
      const companionsSection = document.getElementById('companions-section');
      if (companionsSection) {
        companionsSection.scrollIntoView({ behavior: 'smooth' });
        setActiveTab('companions');
      }
    }
  }, [location.state]);

  // Handle scroll button visibility
  useEffect(() => {
    const handleScroll = () => {
      if (!guideRef.current) return;
      const guidePosition = guideRef.current.getBoundingClientRect().top;
      setShowScrollButton(guidePosition < 0 || guidePosition > window.innerHeight);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]); // Add id as dependency to scroll on plant change

  const scrollToGuide = () => {
    guideRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (error || !plant) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p className="text-red-600">
          {error || 'Plant not found'}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageProgress />
      
      <div className="container mx-auto px-4 py-4">
        <Breadcrumb />
      </div>

      <PlantHero 
        plant={plant}
        isInGarden={isInGarden}
      />

      <div className="sticky top-0 z-10 bg-white border-b">
        <div className="container mx-auto">
          <PlantDetailTabs 
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {activeTab === 'sowing' && <SowingPlanting plant={plant} />}
            {activeTab === 'companions' && (
              <div id="companions-section">
                <Companions plant={plant} />
              </div>
            )}
            {activeTab === 'varieties' && <Varieties plant={plant} />}
            {activeTab === 'maintenance' && <MaintenanceCare plant={plant} />}
            {activeTab === 'harvesting' && <Harvesting plant={plant} />}
            {activeTab === 'storage' && <Storage plant={plant} />}
          </div>

          <div className="space-y-8">
            <div ref={guideRef}>
              <GuideProductCard plantId={plant.id} plantName={plant.name} />
            </div>
            <SeasonalTips plant={plant} />
            <FavoriteUses plant={plant} />
          </div>
        </div>
      </div>

      {/* Floating Guide Button */}
      {showScrollButton && (
        <button
          onClick={scrollToGuide}
          className="fixed bottom-6 right-6 bg-primary-500 text-white p-4 rounded-full shadow-lg hover:bg-primary-600 transition-colors z-50 flex items-center gap-2"
        >
          <Book className="h-5 w-5" />
          <span className="hidden sm:inline">Get the Full Growing Guide!</span>
        </button>
      )}
    </div>
  );
}